import React, { useState, useEffect, useRef } from "react";
import iataLogo  from '../assets/IATA-ATC-PREFERRED_Stamp_RGB_Large.png'
import LazyImage from "../components/common/LazyImage";

const Stats = () => {
  const [inView, setInView] = useState(false);
  const statsRef = useRef(null);

  const statsData = [
    { label: "Expert Instructors", value: 80, suffix: "+", icon: "👨‍🏫" },
    { label: "Placement Rate", value: 98, suffix: "%", icon: "🎯" },
    { label: "Industry Partners", value: 15, suffix: "+", icon: "🤝" },
    { label: "Campus Access", value: 24, suffix: "/7", icon: "🏢" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = statsRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div ref={statsRef} className="w-full pt-20 px-4">
      <div className="max-w-6xl mx-auto">

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-8">
          {statsData.map((stat, index) => (
            <StatCard
              key={index}
              label={stat.label}
              value={stat.value}
              suffix={stat.suffix}
              icon={stat.icon}
              inView={inView}
              delay={index * 0.2}
            />
          ))}
        </div>

        {/* IATA Certification - Clean & Modern */}
        <div className={`pt-8 flex justify-center transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}>
  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 lg:gap-8 px-4 sm:px-6 py-6 sm:py-8 bg-white rounded-2xl max-w-2xl mx-auto">
    {/* Logo */}
    <div className="flex-shrink-0">
      <LazyImage 
        src={iataLogo} 
        alt="IATA Accredited" 
        className="h-28 md:h-32 lg:h-36 w-auto"
        style={{ objectFit: 'contain' }}
      />
    </div>
    
    {/* Divider - Responsive orientation */}
    <div className="w-24 h-0.5 sm:w-0.5 sm:h-20 bg-gradient-to-r sm:bg-gradient-to-b from-transparent via-red-300 to-transparent"></div>
    
    {/* Text Content */}
    <div className="flex flex-col items-center sm:items-start text-center sm:text-left space-y-2">
      <span className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 leading-tight">
        Accredited Training Center
      </span>
      <span className="text-sm sm:text-base text-gray-700 flex items-center gap-2">
        <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        Industry Certified
      </span>
    </div>
  </div>
</div>

      </div>
    </div>
  );
};

const StatCard = ({ label, value, suffix, icon, inView, delay }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;

    let start = 0;
    const duration = 2000;
    const increment = value / (duration / 16);
    let timeoutId;
    let animationId;

    const animateCount = () => {
      start += increment;
      if (start < value) {
        setCount(Math.ceil(start));
        animationId = requestAnimationFrame(animateCount);
      } else {
        setCount(value);
      }
    };

    timeoutId = setTimeout(() => {
      animateCount();
    }, delay * 1000);

    return () => {
      clearTimeout(timeoutId);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [inView, value, delay]);

  return (
    <div
      className={`
        relative p-6 rounded-2xl bg-white 
        transition-all duration-500 transform
        ${inView ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}
      `}
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className="absolute -top-4 left-6 w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center text-2xl transform hover:rotate-0 transition-transform duration-300 -rotate-6">
        {icon}
      </div>
      
      <div className="pt-8">
        <div className="flex items-end justify-start gap-1 mb-3">
          <span className="text-4xl font-bold bg-gradient-to-r from-red-600 to-red-400 bg-clip-text text-transparent">
            {count}
          </span>
          <span className="text-2xl font-semibold text-red-500 mb-1">
            {suffix}
          </span>
        </div>
        
        <div className="text-gray-600 font-medium">
          {label}
        </div>
        
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 to-red-400 rounded-b-2xl opacity-0 scale-x-0 group-hover:scale-x-100 group-hover:opacity-100 transition-all duration-300"/>
      </div>
    </div>
  );
};

export default Stats;